import React, { useEffect, useMemo, useState } from 'react'
import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { useExecOrgParentLuts, useUsRoleTypeLuts, useUsStateLuts } from 'components'
import { ExecOrgAutocomplete } from 'components/us/executives/exec-org-autocomplete'
import { type State, UsStateSelect } from 'components/us/common/state-select'
import {
  type ExecOrgDescendantItem,
  type ExecOrgParentLutItem,
  getExecutiveOrgDescendants
} from 'services/executives'
import { EXECUTIVE_GENERAL_ROLE_IDS, isStateExecutiveRole } from 'utils'

export interface ExecOrgParentSelection {
  roleId: number | null
  state: State | null
  parentOffice: ExecOrgParentLutItem | null
  childOffice: ExecOrgDescendantItem | null
}

export const effectiveParentOrg = (
  selection: ExecOrgParentSelection
): ExecOrgParentLutItem | null => selection.childOffice ?? selection.parentOffice

interface Props {
  value: ExecOrgParentSelection
  onChange: (next: ExecOrgParentSelection) => void
  disabled?: boolean
  excludeIds?: number[]
  roleLabel?: string
  parentOfficeDisabled?: boolean
  allowedRoleIds?: Set<number>
}

export const ExecutiveOrgParentPicker: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  excludeIds,
  roleLabel = 'Role *',
  parentOfficeDisabled = false,
  allowedRoleIds
}) => {
  const { lutValues: roleLut } = useUsRoleTypeLuts()
  const { lutValues: parentLut } = useExecOrgParentLuts()
  const { lutValues: stateLut } = useUsStateLuts()
  const federalAllState: State = stateLut.states.get(0) ?? { id: 0 }
  const { roleId, state, parentOffice, childOffice } = value

  const stateRole = isStateExecutiveRole(roleId)
  const stateEnabled = stateRole && !disabled
  const parentEnabled = !disabled && !parentOfficeDisabled && roleId != null && state != null
  const childEnabled = !disabled && parentOffice != null

  const roleOptions = useMemo(() => (
    Array.from(roleLut.roles.values())
      .filter(role => EXECUTIVE_GENERAL_ROLE_IDS.has(role.id))
      .filter(role => allowedRoleIds == null || allowedRoleIds.has(role.id))
      .sort((a, b) => a.desc.localeCompare(b.desc))
  ), [roleLut.roles, allowedRoleIds])

  const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds])

  const parentOfficeOptions = useMemo<ExecOrgParentLutItem[]>(() => {
    if (roleId == null || state == null) return []
    return Array.from(parentLut.parents.values())
      .filter(parent => parent.role.id === roleId && parent.state.id === state.id && !excluded.has(parent.id))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [parentLut.parents, roleId, state, excluded])

  const [childOptions, setChildOptions] = useState<ExecOrgDescendantItem[]>([])
  const [childLoading, setChildLoading] = useState(false)

  useEffect(() => {
    if (parentOffice == null) {
      setChildOptions([])
      return
    }
    setChildLoading(true)
    void getExecutiveOrgDescendants(parentOffice.id)
      .then(setChildOptions)
      .catch(() => { setChildOptions([]) })
      .finally(() => { setChildLoading(false) })
  }, [parentOffice])

  const handleRoleChange = (nextRoleId: number | null): void => {
    const sameBranch =
      roleId != null && isStateExecutiveRole(nextRoleId) === isStateExecutiveRole(roleId)
    const nextState = sameBranch
      ? state
      : (isStateExecutiveRole(nextRoleId) ? null : federalAllState)
    onChange({
      roleId: nextRoleId,
      state: nextState,
      parentOffice: null,
      childOffice: null
    })
  }

  return (
    <React.Fragment>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>{roleLabel}</InputLabel>
          <Select
            label={roleLabel}
            disabled={disabled}
            value={roleId ?? ''}
            onChange={(event) => {
              const v = event.target.value
              handleRoleChange(v === '' ? null : Number(v))
            }}
          >
            <MenuItem value="">None</MenuItem>
            {roleOptions.map(role => (
              <MenuItem key={role.id} value={role.id}>{role.desc}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <UsStateSelect
            value={state}
            updateState={(next) => {
              onChange({ roleId, state: next, parentOffice: null, childOffice: null })
            }}
            disabled={!stateEnabled}
            onlyUSStates
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <ExecOrgAutocomplete
            label="Parent Office *"
            disabled={!parentEnabled}
            options={parentOfficeOptions}
            value={parentOffice}
            onChange={(next) => {
              onChange({ roleId, state, parentOffice: next, childOffice: null })
            }}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <ExecOrgAutocomplete
            label="Child Office"
            disabled={!childEnabled}
            loading={childLoading}
            options={childOptions.filter(option => !excluded.has(option.id))}
            value={childOffice}
            onChange={(next) => {
              onChange({ roleId, state, parentOffice, childOffice: next })
            }}
          />
        </FormControl>
      </Grid>
    </React.Fragment>
  )
}
